<template>
  <div class="quick-setting-row">
    <div class="quick-setting-label">
      <p class="quick-setting-title">{{ row.label }}</p>
      <p class="quick-setting-tab">{{ tabLabel }}</p>
    </div>

    <div class="quick-setting-control">
      <button
          v-if="row.type === 'boolean'"
          type="button"
          class="quick-setting-switch"
          :class="{ checked: !!modelValue }"
          role="switch"
          :aria-checked="!!modelValue"
          @click="$emit('update:modelValue', !modelValue)"
      >
        <span class="quick-setting-knob"></span>
      </button>

      <div v-else-if="row.type === 'select'" class="quick-setting-select">
        <select
            :value="modelValue"
            @change="$emit('update:modelValue', $event.target.value)"
        >
          <option v-for="option in selectOptions" :key="String(option.value)" :value="option.value">
            {{ option.label ? $t(option.label) : option.text }}
          </option>
        </select>
        <inline-svg class="quick-setting-chevron" :src="require('@/assets/icons/chevron-down.svg')"/>
      </div>

      <div v-else-if="row.type === 'color'" class="quick-setting-color">
        <input
            type="color"
            :value="colorValue"
            @input="$emit('update:modelValue', $event.target.value)"
        >
        <span class="quick-setting-color-value">{{ colorValue }}</span>
      </div>

      <input
          v-else-if="row.type === 'datetime'"
          class="quick-setting-input wide"
          type="datetime-local"
          :value="modelValue"
          @input="$emit('update:modelValue', $event.target.value)"
      >

      <p v-else-if="row.type === 'readonly'" class="quick-setting-readonly">{{ modelValue }}</p>

      <input
          v-else-if="row.type === 'number' || row.type === 'text'"
          class="quick-setting-input"
          :type="isNumber ? 'text' : row.type"
          :inputmode="inputMode"
          :value="modelValue"
          @input="onInput"
      >

      <button v-else type="button" class="quick-setting-link" @click="$emit('open', row)">
        {{ $t('quickMenu.openInSettings') }}
      </button>
    </div>
  </div>
</template>

<script>
import {applyDecimalInput, applyIntegerInput} from "@/helpers/numericInput";

export default {
  name: "QuickSettingRow",
  props: {
    row: {type: Object, required: true},
    modelValue: {default: null},
    sourceOptions: {type: Array, default: () => ([])},
  },
  emits: ["update:modelValue", "open"],
  computed: {
    isNumber() {
      return this.row.type === "number";
    },
    inputMode() {
      if (!this.isNumber) return null;
      return this.row.decimal ? "decimal" : "numeric";
    },
    selectOptions() {
      if (this.row.source) return this.sourceOptions;
      return (this.row.options || []).map((option) => ({...option, text: option.value}));
    },
    colorValue() {
      const value = String(this.modelValue || "");
      return /^#[0-9a-fA-F]{6}$/.test(value) ? value : "#000000";
    },
    tabLabel() {
      if (!this.row.tabTitle) return "";
      return this.row.tabTitle.includes(".") ? this.$t(this.row.tabTitle) : this.row.tabTitle;
    }
  },
  methods: {
    onInput(event) {
      if (this.isNumber) {
        this.$emit("update:modelValue", this.row.decimal ? applyDecimalInput(event) : applyIntegerInput(event));
        return;
      }
      this.$emit("update:modelValue", event.target.value);
    }
  }
}
</script>

<style lang="scss" scoped>
.quick-setting-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  column-gap: $default-edge-spacing-xl;
  min-height: 44px;
  padding: $default-edge-spacing $default-edge-spacing-xl;
  border-bottom: 1px solid $color-separator;
  border-left: 2px solid transparent;
  transition: border-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:focus-within {
    border-left-color: $color-primary-blue;

    .quick-setting-title {
      color: $color-white-text;
    }
  }
}

.quick-setting-label {
  min-width: 0;
  flex: 1 1 auto;
}

.quick-setting-title {
  margin: 0;
  color: $color-primary-text;
  font-size: $font-subtitle;
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.quick-setting-tab {
  margin: 0;
  color: $color-secondary-text;
  font-size: 11px;
}

.quick-setting-control {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.quick-setting-switch {
  position: relative;
  width: 44px;
  height: 26px;
  padding: 0;
  border: none;
  border-radius: 13px;
  background: $color-input-back-solid;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &.checked {
    background: $color-primary-blue;
  }

  &:focus-visible {
    outline: 2px solid $color-primary-blue;
    outline-offset: 2px;
  }
}

.quick-setting-knob {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: $color-white-text;
  transition: transform 0.2s ease;

  .quick-setting-switch.checked & {
    transform: translateX(18px);
  }
}

.quick-setting-select {
  position: relative;
  display: flex;
  align-items: center;

  select {
    height: 30px;
    min-width: 150px;
    padding: 0 30px 0 12px;
    border: 1px solid $color-separator;
    border-radius: $radius-small;
    background: transparent;
    color: $color-white-text;
    font-size: $font-small;
    appearance: none;
    cursor: pointer;

    &:focus-visible {
      outline: none;
      border-color: $color-primary-blue;
      box-shadow: 0 0 0 2px rgba(131, 147, 244, 0.35);
    }

    option {
      background: $color-card-back;
      color: $color-white-text;
    }
  }
}

.quick-setting-chevron {
  position: absolute;
  right: 10px;
  width: 14px;
  height: 14px;
  color: $color-secondary-text;
  pointer-events: none;
}

.quick-setting-color {
  display: flex;
  align-items: center;
  gap: $default-edge-spacing;

  input {
    width: 30px;
    height: 30px;
    padding: 0;
    border: 1px solid $color-separator;
    border-radius: $radius-small;
    background: transparent;
    cursor: pointer;

    &:focus-visible {
      outline: 2px solid $color-primary-blue;
      outline-offset: 2px;
    }
  }
}

.quick-setting-color-value {
  color: $color-secondary-text;
  font-size: $font-small;
}

.quick-setting-readonly {
  margin: 0;
  color: $color-secondary-text;
  font-size: $font-small;
}

.quick-setting-input {
  height: 30px;
  width: 150px;
  padding: 0 12px;
  border: 1px solid $color-separator;
  border-radius: $radius-small;
  background: transparent;
  color: $color-white-text;
  font-size: $font-small;
  text-align: right;

  &.wide {
    width: 190px;
  }

  &:focus-visible {
    outline: none;
    border-color: $color-primary-blue;
    box-shadow: 0 0 0 2px rgba(131, 147, 244, 0.35);
  }
}

.quick-setting-link {
  border: none;
  border-radius: $radius-small;
  padding: 6px 12px;
  font-size: $font-small;
  color: $color-primary-blue;
  background: transparent;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $color-primary-blue;
    outline-offset: 2px;
  }
}

@media (max-width: $screen-md) {
  .quick-setting-row {
    padding: $default-edge-spacing $default-edge-spacing-l;
  }

  .quick-setting-select select,
  .quick-setting-input {
    min-width: 0;
    width: 120px;
  }
}
</style>
